import type React from "react";
import { useEffect, useState } from "react";
import {
  Alert,
  Box, Button, Card, Chip, CircularProgress, Collapse,
  Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton,
  Stack, Tab, Tabs, Table, TableBody, TableCell, TableHead, TableRow,
  Tooltip, Typography,
} from "@mui/material";
import {
  Add, Close, Description, Download, ExpandLess, ExpandMore, People,
  PlayCircleOutlined, UnfoldLess, UnfoldMore,
} from "@mui/icons-material";
import { useParams } from "react-router-dom";
import { useCohortAuditLog } from "@/api/hooks/auditLog";
import { useClinOpsView } from "@/api/hooks/clinopsView";
import type {
  DocumentRow, InvestigatorAssignment, LocationArtifactRow, LocationInDA,
  Milestone, MilestoneStatus, StudyRead,
} from "@/api/types";
import { brand } from "@/theme/brandColors";
import { CohortInvestigators } from "./CohortInvestigators";
import { LocationDocumentsTable } from "./LocationDocumentsTable";
import { LocationPersonnelTable } from "./LocationPersonnelTable";
import { MilestoneBar } from "./MilestoneBar";
import {
  RollupStatusChip, rollupMilestoneStatuses,
} from "./RollupStatusChip";

const DOT_COLOR: Record<MilestoneStatus, string> = {
  green: "#16a34a",
  yellow: "#eab308",
  red: "#dc2626",
  gray: "#d1d5db",
};

/** Aggregate per-location participation_status into a single
 * cohort-level pill, mirroring derive_pharma_site_status on the
 * backend. ``active`` if any location is activated; ``cancelled``
 * iff every location is cancelled; ``pending`` otherwise. */
function deriveCohortStatus(
  locations: { participation_status: string | null }[],
): "active" | "pending" | "cancelled" {
  if (locations.length === 0) return "pending";
  const statuses = locations.map((l) => l.participation_status);
  if (statuses.every((s) => s === "cancelled")) return "cancelled";
  if (statuses.some((s) => s === "activated")) return "active";
  return "pending";
}

const COHORT_STATUS_COLOR: Record<
  "active" | "pending" | "cancelled",
  "success" | "warning" | "default"
> = {
  active: "success",
  pending: "warning",
  cancelled: "default",
};

/** Capitalize the participation status and append the activation date
 * when present, e.g. ``Activated 2026-05-22``, ``Pending kickoff``. */
export function formatStatusLine(
  participationStatus: string | null,
  activatedAt: string | null,
): string | null {
  if (!participationStatus) return null;
  const human = participationStatus.replace("_", " ");
  const capitalized = human.charAt(0).toUpperCase() + human.slice(1);
  return activatedAt ? `${capitalized} ${activatedAt}` : capitalized;
}

interface Props {
  /** The DA's MASTER study — surfaces in the outer header strip and
   * the inner card's protocol-number area. Null when the DA doesn't
   * have a master yet (rare). */
  masterStudy: StudyRead | null;
  locations: LocationInDA[];
  locationDocuments: Record<string, LocationArtifactRow[]>;
  onAdd: () => void;
  onRemove: (locationId: string) => void;
  /** Fired when the operator clicks "Activate cohort" on a non-
   * activated location row. Parent kicks the force-activate-cohort
   * endpoint and refetches. */
  onActivateCohort: (participationId: string) => void;
  activatingParticipationId?: string | null;
  /** Cohort-scoped milestone pills. Rendered above the location
   * list. Optional — pass empty array to hide. */
  milestones?: Milestone[];
  /** PI / Sub-I assignment data. Renders a CohortInvestigators row
   * above the location list when provided. */
  pi?: InvestigatorAssignment | null;
  subInvestigators?: InvestigatorAssignment[];
  piDocuments?: DocumentRow[];
  subInvestigatorDocuments?: Record<string, DocumentRow[]>;
  onAddSub?: () => void;
  onReplacePI?: () => void;
  onRemoveInvestigator?: (assignmentId: string) => void;
}

/**
 * Cohort panel — laid out as the sponsored-studies wrapper applied
 * to a single study. Outer chrome shows ``<master-study-name> · 1``
 * in the gray header strip; the inner card mirrors PharmaStudyCard
 * (status pill on the right, subhead caption, milestones, location
 * chips). Each location chip carries a collapsible Documents +
 * Personnel panel that starts closed.
 */
export function LocationsInDAPanel({
  masterStudy, locations, locationDocuments, onAdd, onRemove,
  onActivateCohort, activatingParticipationId,
  milestones = [], pi = null, subInvestigators = [],
  piDocuments = [], subInvestigatorDocuments = {},
  onAddSub, onReplacePI, onRemoveInvestigator,
}: Props) {
  const [etmfOpen, setEtmfOpen] = useState(false);
  const [open, setOpen] = useState(true);
  const showInvestigators = onAddSub !== undefined && onReplacePI !== undefined
    && onRemoveInvestigator !== undefined;
  const cohortStatus = deriveCohortStatus(locations);

  return (
    <Card variant="outlined" sx={{ p: 0 }}>
      {/* Outer header strip mirrors ``Pharma studies · N`` on
       * PharmaStudiesPanel — uses the master-study name in place of
       * the generic "Pharma studies" label. Clicking the strip
       * collapses the inner card body. */}
      <Box
        onClick={() => setOpen((v) => !v)}
        role="button"
        aria-expanded={open}
        aria-controls="cohort-card-body"
        sx={{
          p: 1.5, borderBottom: open ? 1 : 0, borderColor: "divider",
          bgcolor: "action.hover",
          cursor: "pointer", userSelect: "none",
          display: "flex", alignItems: "center", gap: 1,
        }}
      >
        <Typography sx={{ fontWeight: 600, fontSize: 13 }}>
          {masterStudy?.name ?? "Cohort"} · 1
        </Typography>
        <Box sx={{ flex: 1 }} />
        <IconButton size="small" aria-label={open ? "Collapse master protocol" : "Expand master protocol"}>
          {open ? <UnfoldLess fontSize="small" /> : <UnfoldMore fontSize="small" />}
        </IconButton>
      </Box>

      <Collapse in={open} unmountOnExit>
      {/* Inner study card — same layout as PharmaStudyCard. */}
      <Box id="cohort-card-body" sx={{ p: 2 }}>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", mb: 1 }}>
          {/* Sponsor column intentionally empty for the cohort —
              cohorts are TargetRWE-owned, not sponsor-driven. */}
          {masterStudy?.protocol_number && (
            <Typography sx={{
              fontFamily: "ui-monospace, Menlo, monospace",
              color: "text.secondary",
              fontSize: 13,
            }}>
              {masterStudy.protocol_number}
            </Typography>
          )}
          <Box sx={{ flex: 1 }} />
          {/* Cohort top-level status pill uses brand citron for the
              "active" case — brighter than the standard success green
              so it reads as the headline state. Other cases keep MUI's
              default mapping. */}
          <Chip
            size="small"
            label={cohortStatus}
            color={COHORT_STATUS_COLOR[cohortStatus]}
            sx={cohortStatus === "active"
              ? { bgcolor: brand.citron, color: brand.black, borderColor: brand.citron }
              : undefined}
          />
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          {masterStudy?.phase ? `${masterStudy.phase} · ` : ""}
          {masterStudy?.study_type ?? "—"}
          {masterStudy?.status ? ` · Study status: ${masterStudy.status}` : ""}
          {pi ? ` · PI ${pi.investigator.last_name}` : " · PI unassigned"}
        </Typography>

        {milestones.length > 0 && (
          <Box sx={{ mb: 1 }}>
            <MilestoneBar milestones={milestones} />
          </Box>
        )}

        {showInvestigators && (
          <Box sx={{ mb: 2 }}>
            <CohortInvestigators
              pi={pi}
              subInvestigators={subInvestigators}
              piDocuments={piDocuments}
              subInvestigatorDocuments={subInvestigatorDocuments}
              onAddSub={onAddSub!}
              onReplacePI={onReplacePI!}
              onRemoveSub={onRemoveInvestigator!}
            />
          </Box>
        )}

        <Box sx={{ mt: 2 }}>
          <CohortLocationsPanel
            locations={locations}
            locationDocuments={locationDocuments}
            onAdd={onAdd}
            onRemove={onRemove}
            onActivateCohort={onActivateCohort}
            activatingParticipationId={activatingParticipationId ?? null}
          />
        </Box>

        {/* eTMF shortcut — sits on a dashed divider as a follow-on
            action below the locations panel. */}
        <Box
          sx={{
            mt: 1.5, pt: 1.5,
            borderTop: 1, borderStyle: "dashed", borderColor: "divider",
            display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap",
          }}
        >
          <Box sx={{ flex: 1 }} />
          <Button
            size="small"
            startIcon={<Description fontSize="small" />}
            sx={{ fontStyle: "italic", color: "primary.main" }}
            onClick={() => setEtmfOpen(true)}
          >
            eTMF
          </Button>
        </Box>
      </Box>
      </Collapse>

      <ETMFPreviewDialog
        open={etmfOpen}
        onClose={() => setEtmfOpen(false)}
        masterStudy={masterStudy}
        locations={locations}
      />
    </Card>
  );
}

/**
 * Tabbed + collapsible panel for the cohort's enrolled locations.
 * One tab per location with the name + a row of artifact-rollup
 * dots in the tab label. Selecting a tab swaps in that location's
 * Documents + Personnel underneath. Mirrors CohortInvestigators
 * visually so the cohort body reads as a stack of two consistent
 * panels (Investigators + Locations).
 */
function CohortLocationsPanel({
  locations, locationDocuments, onAdd, onRemove,
  onActivateCohort, activatingParticipationId,
}: {
  locations: LocationInDA[];
  locationDocuments: Record<string, LocationArtifactRow[]>;
  onAdd: () => void;
  onRemove: (locationId: string) => void;
  onActivateCohort: (participationId: string) => void;
  activatingParticipationId: string | null;
}) {
  const [activeId, setActiveId] = useState<string | null>(
    locations[0]?.location_id ?? null,
  );
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (locations.length === 0) {
      if (activeId !== null) setActiveId(null);
      return;
    }
    if (!locations.some((l) => l.location_id === activeId)) {
      setActiveId(locations[0].location_id);
    }
  }, [locations, activeId]);

  const active = locations.find((l) => l.location_id === activeId) ?? null;

  return (
    <Card variant="outlined" sx={{ p: 0 }}>
      <Box
        onClick={() => setOpen((v) => !v)}
        role="button"
        aria-expanded={open}
        aria-controls="cohort-locations-body"
        sx={{
          p: 1.5, borderBottom: open ? 1 : 0, borderColor: "divider",
          bgcolor: brand.aqua,
          color: brand.pearl,
          cursor: "pointer", userSelect: "none",
          display: "flex", alignItems: "center", gap: 1,
        }}
      >
        <Typography sx={{ fontWeight: 600, fontSize: 13, color: brand.pearl }}>
          Locations · {locations.length}
        </Typography>
        <RollupStatusChip
          level={rollupMilestoneStatuses(
            locations.flatMap((l) => l.artifact_rollup),
          )}
        />
        <Box sx={{ flex: 1 }} />
        <Button
          size="small"
          startIcon={<Add />}
          onClick={(e) => { e.stopPropagation(); onAdd(); }}
          sx={{ color: brand.pearl, "&:hover": { bgcolor: "rgba(255,255,255,0.1)" } }}
        >
          Add location
        </Button>
        <IconButton
          size="small"
          aria-label={open ? "Collapse locations" : "Expand locations"}
          sx={{ color: brand.pearl }}
        >
          {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        </IconButton>
      </Box>

      <Collapse in={open} unmountOnExit>
      <Box id="cohort-locations-body">
        {locations.length === 0 ? (
          <Box sx={{ p: 3, textAlign: "center", color: "text.secondary", fontSize: 13 }}>
            No locations enrolled in this cohort yet.
          </Box>
        ) : (
          <>
            <Tabs
              value={active?.location_id ?? false}
              onChange={(_, v) => setActiveId(v as string)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ borderBottom: 1, borderColor: "divider" }}
            >
              {locations.map((loc) => (
                <Tab
                  key={loc.location_id}
                  value={loc.location_id}
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box component="span" sx={{ fontWeight: 500 }}>
                        {loc.name}
                      </Box>
                      <Stack direction="row" spacing={0.5}>
                        {loc.artifact_rollup.map((c, i) => (
                          <Box
                            key={i}
                            sx={{
                              width: 9, height: 9, borderRadius: "50%",
                              bgcolor: DOT_COLOR[c],
                            }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  }
                  sx={{ textTransform: "none", minHeight: 44 }}
                />
              ))}
            </Tabs>

            {active && (
              <Box sx={{ p: 2 }}>
                {/* Identity caption + status pill row. Identity is in
                    the active tab, so this row just carries the
                    status / activation date plus the Activate / Remove
                    actions. */}
                <Box
                  sx={{
                    display: "flex", alignItems: "center", gap: 1,
                    mb: 2, flexWrap: "wrap",
                  }}
                >
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                      Location ID: {active.display_id}
                    </Typography>
                    {(() => {
                      const isActive = active.participation_status === "activated";
                      const statusLine = formatStatusLine(
                        active.participation_status,
                        isActive ? active.activated_at : null,
                      );
                      return statusLine ? (
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                          {statusLine}
                        </Typography>
                      ) : null;
                    })()}
                  </Box>
                  <Box sx={{ flex: 1 }} />
                  {active.participation_id &&
                    active.participation_status !== "activated" && (
                      <Tooltip title="Set the cohort participation to phase 4 / ACTIVATED so this location can be enrolled in sponsored studies. Bypasses the task-status gating.">
                        <span>
                          <Button
                            size="small"
                            variant="outlined"
                            color="success"
                            startIcon={<PlayCircleOutlined fontSize="small" />}
                            disabled={
                              activatingParticipationId === active.participation_id
                            }
                            onClick={() => onActivateCohort(active.participation_id!)}
                          >
                            {activatingParticipationId === active.participation_id
                              ? "Activating…"
                              : "Activate cohort"}
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                  <Button
                    size="small"
                    color="error"
                    onClick={() => onRemove(active.location_id)}
                  >
                    Remove
                  </Button>
                </Box>

                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ display: "block", mb: 0.5, fontWeight: 600 }}
                >
                  Documents
                </Typography>
                <LocationDocumentsTable
                  rows={locationDocuments[active.location_id] ?? []}
                  locationId={active.location_id}
                />

                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ display: "block", mt: 3, mb: 0.5, fontWeight: 600 }}
                >
                  Personnel · cohort
                </Typography>
                {active.participation_id ? (
                  <LocationPersonnelTable
                    participationId={active.participation_id}
                    enabled
                    locationId={active.location_id}
                  />
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    This location isn't enrolled in the cohort yet.
                  </Typography>
                )}
              </Box>
            )}
          </>
        )}
      </Box>
      </Collapse>
    </Card>
  );
}

/**
 * eTMF audit-trail viewer. First piece of the eTMF — every mutation
 * that touched the cohort (participations, tasks, location artifacts,
 * documents, investigator assignments) is fetched scoped to (site, DA)
 * and shown in a table. ``Download CSV`` hits the streaming endpoint
 * directly so the browser's native download flow kicks in. Full bundle
 * (zip of source docs) lands in a follow-on chunk.
 */
function ETMFPreviewDialog({
  open,
  onClose,
  masterStudy,
}: {
  open: boolean;
  onClose: () => void;
  masterStudy: StudyRead | null;
  /** Kept on the prop type for the future bundle work — not used by
   * the audit-trail view but the parent still passes it through. */
  locations: LocationInDA[];
}) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pr: 1 }}>
        <Description fontSize="small" />
        <Box sx={{ flex: 1 }}>
          eTMF · {masterStudy?.name ?? "Cohort"}
          {masterStudy?.protocol_number && (
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
              sx={{ ml: 1, fontFamily: "ui-monospace, Menlo, monospace" }}
            >
              {masterStudy.protocol_number}
            </Typography>
          )}
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <ETMFAuditTrail enabled={open} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Audit-trail table rendered inside the eTMF dialog. Pulls cohort-
 * scoped events from the read endpoint and offers a CSV download
 * pointing at the streaming export endpoint.
 */
function ETMFAuditTrail({ enabled }: { enabled: boolean }) {
  const { id: siteId, da_code: daCode } = useParams<{ id: string; da_code: string }>();
  const view = useClinOpsView(siteId, daCode);
  const daId = view.data?.disease_area.id;
  const audit = useCohortAuditLog(siteId, daId, { enabled: enabled && Boolean(daId) });

  const csvHref = siteId && daId
    ? `/api/sites/${siteId}/cohorts/${daId}/audit-events.csv`
    : null;

  return (
    <Stack spacing={1.5}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
          Audit trail
        </Typography>
        <Box sx={{ flex: 1 }} />
        {csvHref && (
          <Button
            size="small"
            startIcon={<Download fontSize="small" />}
            component="a"
            href={csvHref}
            download
          >
            Download CSV
          </Button>
        )}
      </Box>

      {audit.isLoading && (
        <Box sx={{ textAlign: "center", py: 3 }}>
          <CircularProgress size={20} />
        </Box>
      )}
      {audit.error && (
        <Alert severity="error">
          Could not load audit trail: {(audit.error as Error).message}
        </Alert>
      )}
      {audit.data && audit.data.items.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No audit events recorded for this cohort yet.
        </Typography>
      )}
      {audit.data && audit.data.items.length > 0 && (
        <Box sx={{ maxHeight: 480, overflowY: "auto" }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 180 }}>When</TableCell>
                <TableCell sx={{ width: 170 }}>Entity</TableCell>
                <TableCell>Event</TableCell>
                <TableCell sx={{ width: 220 }}>Change</TableCell>
                <TableCell sx={{ width: 220 }}>Target</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {audit.data.items.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell sx={{ fontFamily: "ui-monospace, Menlo, monospace", fontSize: 12 }}>
                    {new Date(r.at).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" variant="outlined" label={r.entity_type} />
                  </TableCell>
                  <TableCell>{r.event_type}</TableCell>
                  <TableCell>
                    {r.from_state || r.to_state ? (
                      <Typography variant="body2" sx={{ fontFamily: "ui-monospace, Menlo, monospace", fontSize: 12 }}>
                        {r.from_state ?? "—"} → {r.to_state ?? "—"}
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "ui-monospace, Menlo, monospace" }}>
                      {r.target_label ?? r.entity_id.slice(0, 8)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Stack>
  );
}
